import { computed, observable, action } from 'mobx';
import ConfigRepository from '../../repositories/ConfigRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import Transaction from '../../domain/entities/Transaction/Transaction';
import Config, { IConvenienceStoreProvider, CONVENIENCE_STORE } from '../../domain/entities/Config';
import { DeliveryOptions } from '../../constants/Constants';
import ConvenienceStore from '../../domain/entities/ConvenienceStore/ConvenienceStore';
import RedirectUserUseCase from '../../use-cases/RedirectUserUseCase';
import UIState from '../../stores/UIState';
import { CVS_NOTIFICATIONS, NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';

interface IConvenienceStoreViewControllerData {
  convenienceStore: ConvenienceStore;
  convenienceStoreProviders: IConvenienceStoreProvider[];
  selectedProviderName?: CONVENIENCE_STORE;
  isMobile: boolean;
  baseUrl: string;
  transId: number | null;
  hasErrors: boolean;
}

interface IConvenienceStoreViewControllerConfig {
  configRepository: ConfigRepository;
  transactionRepository: TransactionRepository;
  uiState: UIState;
  notificationsCollection: NotificationsCollection;
}

export default class ConvenienceStoreViewController {
  private config: Config;

  @observable transaction: Transaction;

  private configRepository: ConfigRepository;

  private transactionRepository: TransactionRepository;

  private notificationsCollection: NotificationsCollection;

  private uiState: UIState;

  @observable selectedProvider: IConvenienceStoreProvider;

  constructor(config: IConvenienceStoreViewControllerConfig) {
    const { configRepository, transactionRepository, uiState, notificationsCollection } = config;
    this.configRepository = configRepository;
    this.transactionRepository = transactionRepository;
    this.uiState = uiState;
    this.notificationsCollection = notificationsCollection;
  }

  async loaded(): Promise<void> {
    this.config = await this.configRepository.getConfig();
    this.transaction = await this.transactionRepository.getTransaction();

    const providers = this.storeProviders;

    if (providers?.length === 1) {
      // eslint-disable-next-line prefer-destructuring
      this.selectedProvider = providers[0];
      return;
    }

    const selectedStoreType = this.transaction?.convenienceStore?.storeType;
    const defaultStoreType = providers?.find(provider => provider.isDefault)?.name;
    const storeType = selectedStoreType || defaultStoreType;

    if (storeType) {
      const matched = providers?.find(provider => provider.name === storeType);
      if (matched) {
        this.selectedProvider = matched;
      }
    }

    this.notificationsCollection.clearNotifications(
      NOTIFICATION_GROUP.CVS,
      NOTIFICATION_TYPE.DANGER,
    );
    if ((this.transaction?.orderSummary?.totalCost ?? 0) >= (this.config?.convenienceStoreLimit || 0)) {
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.CVS,
        key: CVS_NOTIFICATIONS.EXCEEDS_STORE_LIMIT,
        type: NOTIFICATION_TYPE.DANGER,
      });
    }
  }

  @computed
  public get hasErrors(): boolean {
    return this.notificationsCollection.hasNotificationsByGroup(NOTIFICATION_GROUP.CVS, NOTIFICATION_TYPE.DANGER);
  }


  @computed
  public get storeProviders(): IConvenienceStoreProvider[] {
    return this.transaction?.deliveryOptions
      ?.find(deliveryOption => deliveryOption.name === DeliveryOptions.CONVENIENCE_STORE_PICK_UP)
      ?.config?.convenienceStoreProviders ?? [];
  }

  @computed
  public get data(): IConvenienceStoreViewControllerData {
    return {
      baseUrl: this.config?.baseUrl ?? '',
      convenienceStore: this.transaction?.convenienceStore,
      convenienceStoreProviders: this.storeProviders,
      hasErrors: this.hasErrors,
      isMobile: this.uiState.isMobile,
      selectedProviderName: this.selectedProvider?.name,
      transId: this.transaction?.transId ?? null,
    };
  }

  @action
  public onSelectProvider = (providerName: CONVENIENCE_STORE): void => {
    this.selectedProvider = this.storeProviders.find(provider => provider.name === providerName);
  };

  public onSelectStore = (): void => {
    if (this.selectedProvider) {
      const { name: providerName } = this.selectedProvider;
      const { baseUrl } = this.config;
      let redirectUrl = '';
      if (providerName == CONVENIENCE_STORE.SAGAWA) {
        redirectUrl = this.selectedProvider.url.replace(/::base_url::/gi, baseUrl);
      }

      if (providerName == CONVENIENCE_STORE.FAMILY_MART) {
        //sample redirect url: https://mfme.map.com.tw/default.aspx?cvsname=eltw_eng_m&cvsid=93670521&cvstemp=&exchange=true
        const { postUrl, mobileCvsNameSuffix, cvsName } = this.selectedProvider;
        const cvsid = encodeURIComponent(this.transaction.transId || '');
        const cvsname = encodeURIComponent(this.uiState.isMobile ? (cvsName + mobileCvsNameSuffix) : cvsName);
        redirectUrl = `${postUrl}cvsname=${cvsname}&cvsid=${cvsid}&cvstemp=&exchange=true`;
      }

      new RedirectUserUseCase(redirectUrl).execute();
      
    }
  };
}
